import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckIcon } from "lucide-react";
import type { AnswerState, MemoryExercise } from "../../types/exercises";
import { shuffle } from "../../utils/text";
import { speak } from "../../utils/speech";
import { spring } from "../../utils/motion";
interface MemoryCardsProps {
  exercise: MemoryExercise;
  onAnswer: (a: AnswerState) => void;
  speechLang: string;
}
interface MemoryCard {
  key: string;
  pair: number;
  text: string;
  side: "term" | "translation";
}
export function MemoryCards({
  exercise,
  onAnswer,
  speechLang
}: MemoryCardsProps) {
  const reduce = useReducedMotion() ?? false;
  const [cards] = useState<MemoryCard[]>(() => shuffle(exercise.pairs.flatMap((p, i) => [{
    key: `t${i}`,
    pair: i,
    text: p.term,
    side: "term" as const
  }, {
    key: `r${i}`,
    pair: i,
    text: p.translation,
    side: "translation" as const
  }])));
  const [open, setOpen] = useState<string[]>([]);
  const [matched, setMatched] = useState<Set<number>>(new Set());
  const [moves, setMoves] = useState(0);
  useEffect(() => {
    if (open.length !== 2) return;
    const [a, b] = open.map(k => cards.find(c => c.key === k)!);
    setMoves(m => m + 1);
    const ok = a.pair === b.pair;
    const t = window.setTimeout(() => {
      if (ok) setMatched(m => new Set(m).add(a.pair));
      setOpen([]);
    }, ok ? 420 : 900);
    return () => window.clearTimeout(t);
  }, [open, cards]);
  useEffect(() => {
    if (matched.size === exercise.pairs.length) onAnswer({
      ready: true,
      correct: true,
      auto: true
    });
  }, [matched, exercise.pairs.length, onAnswer]);
  const flip = (c: MemoryCard) => {
    if (open.length >= 2 || open.includes(c.key) || matched.has(c.pair)) return;
    if (c.side === "term") speak(c.text, speechLang);
    setOpen(o => [...o, c.key]);
  };
  return <div data-id="element-391" data-mp="1j6645wthdyhe">
      <div className="mb-4 flex justify-between text-sm font-semibold text-ink-2" aria-live="polite" data-id="element-392" data-mp="1j5we698frrbp">
        <span data-id="element-393" data-mp="tloi5ygsf4ic">
          Pairs <span className="text-ink" data-id="element-394" data-mp="okimpsvqt354">{matched.size}</span> / {exercise.pairs.length}
        </span>
        <span data-id="element-395" data-mp="13hrhyxo62r24">
          Moves <span className="text-ink" data-id="element-396" data-mp="13ib1gitgtni9">{moves}</span>
        </span>
      </div>
      <ul className="grid grid-cols-3 gap-2.5 md:grid-cols-4 md:gap-4" data-id="element-397" data-mp="yioomahju99z">
        {cards.map(c => {
        const faceUp = open.includes(c.key) || matched.has(c.pair);
        const isMatched = matched.has(c.pair);
        return <li key={c.key} className="aspect-[4/5]" style={{
          perspective: 900
        }} data-id="element-398" data-mp="5v29q62vfi7t">
              <button type="button" onClick={() => flip(c)} aria-label={faceUp ? c.text : "Hidden card"} disabled={isMatched} className="focus-ring h-full w-full rounded-card" data-id="element-399" data-mp="kj5ghhoqmxxo">
                <motion.div className="preserve-3d relative h-full w-full" animate={{
              rotateY: reduce ? 0 : faceUp ? 180 : 0,
              scale: isMatched ? 0.96 : 1
            }} transition={spring.smooth} data-id="element-400" data-mp="3j8x8j3mt0oza">
                  {(!reduce || !faceUp) && <div className="backface-hidden tex-dots absolute inset-0 grid place-items-center rounded-card border-3 border-line bg-brand text-3xl font-bold text-white shadow-brutal-sm" data-id="element-401" data-mp="1osyv0m7m7tfy">
                      P
                    </div>}
                  {(!reduce || faceUp) && <div className={`backface-hidden absolute inset-0 grid place-items-center rounded-card border-3 border-line p-2 text-center text-sm font-semibold shadow-brutal-sm md:text-base ${isMatched ? "bg-good-tint text-ink" : c.side === "term" ? "bg-surface text-ink" : "bg-accent text-night"}`} style={{
                transform: reduce ? undefined : "rotateY(180deg)"
              }} lang={c.side === "term" ? "es" : undefined} data-id="element-402" data-mp="2mgz7feq9qc1w">
                      {c.text}
                      {isMatched && <CheckIcon className="absolute right-2 top-2 h-4 w-4 text-good" strokeWidth={3} data-id="element-403" data-mp="1cjso3ry3e3m9" />}
                    </div>}
                </motion.div>
              </button>
            </li>;
      })}
      </ul>
    </div>;
}